/**
 * Original line-icon glyphs for the activity catalogue. Every glyph is drawn
 * for a 24×24 viewBox and inherits the shared shell (stroke 1.8, round caps,
 * currentColor) from ActivityIcon. Keys are the catalogue `iconId` values.
 */
import type { ReactNode } from 'react'

export const ACTIVITY_GLYPHS: Record<string, ReactNode> = {
  // -- Eten en drinken ------------------------------------------------------
  bowl: (
    <>
      <path d="M4 12h16a8 8 0 0 1-16 0z" />
      <path d="M9 8c0-1.4 1.2-1.4 1.2-2.8M13.8 8c0-1.4 1.2-1.4 1.2-2.8" />
    </>
  ),
  plate: (
    <>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="3.4" />
    </>
  ),
  cloche: (
    <>
      <path d="M4 16a8 8 0 0 1 16 0z" />
      <path d="M2.5 19.5h19" />
      <path d="M12 8V6.8" />
      <circle cx="12" cy="5.6" r="0.9" />
    </>
  ),
  apple: (
    <>
      <path d="M12 7.5c-4.6-2.4-8 .6-8 4.7 0 4 3 8 5.4 8 1 0 1.6-.5 2.6-.5s1.6.5 2.6.5c2.4 0 5.4-4 5.4-8 0-4.1-3.4-7.1-8-4.7z" />
      <path d="M12 7.5c0-2 1-3.6 2.8-4.3" />
    </>
  ),
  'glass-water': (
    <>
      <path d="M6 3.5h12l-1.4 15a2 2 0 0 1-2 1.8H9.4a2 2 0 0 1-2-1.8z" />
      <path d="M6.8 10.5h10.4" />
    </>
  ),
  coffee: (
    <>
      <path d="M5 9h11v6.5A4.5 4.5 0 0 1 11.5 20h-2A4.5 4.5 0 0 1 5 15.5z" />
      <path d="M16 10h1.6a2.6 2.6 0 0 1 0 5.2H16" />
      <path d="M8.5 3.5c0 1.2 1.4 1.2 1.4 2.4M12.5 3.5c0 1.2 1.4 1.2 1.4 2.4" />
    </>
  ),
  tea: (
    <>
      <path d="M5 8.5h11v7A4.5 4.5 0 0 1 11.5 20h-2A4.5 4.5 0 0 1 5 15.5z" />
      <path d="M16 9.5h1.6a2.6 2.6 0 0 1 0 5.2H16" />
      <path d="M10.5 3v3.5" />
      <path d="M9 6.5h3l-.4 3.2H9.4z" />
    </>
  ),
  smoothie: (
    <>
      <path d="M7 8h10l-1.2 11a2 2 0 0 1-2 1.7h-3.6a2 2 0 0 1-2-1.7z" />
      <path d="M6 8h12" />
      <path d="M14.5 3 12 8" />
    </>
  ),
  pot: (
    <>
      <path d="M5 10h14v5a5 5 0 0 1-5 5h-4a5 5 0 0 1-5-5z" />
      <path d="M2.5 10h19" />
      <path d="M9.5 6.8c0-1.1 1-1.1 1-2.2M13.5 6.8c0-1.1 1-1.1 1-2.2" />
    </>
  ),
  'cutting-board': (
    <>
      <rect x="3.5" y="7" width="13.5" height="11" rx="2" />
      <path d="M17 10h2a1.8 1.8 0 0 1 0 3.6h-2" />
      <path d="M7 11l3.5 3.5" />
    </>
  ),
  // -- Gezondheid -----------------------------------------------------------
  pill: (
    <>
      <path d="M10.3 20.7a4.9 4.9 0 0 1-7-6.9l7-7a4.9 4.9 0 0 1 6.9 7l-7 6.9z" />
      <path d="M8.7 8.7l6.6 6.6" />
    </>
  ),
  bottle: (
    <>
      <path d="M10 3h4" />
      <path d="M10.8 3v3.2L8.5 8.6a2 2 0 0 0-.5 1.3V19a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V9.9a2 2 0 0 0-.5-1.3l-2.3-2.4V3" />
      <path d="M8 13.5h8" />
    </>
  ),
  jar: (
    <>
      <path d="M8 3.5h8" />
      <path d="M8.7 3.5v2.1L7 7.4V19a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V7.4l-1.7-1.8V3.5" />
      <path d="M7 12h10" />
    </>
  ),
  pharmacy: (
    <>
      <path d="M4 9.5 12 4l8 5.5" />
      <path d="M6 10.5V19h12v-8.5" />
      <path d="M12 12v5M9.5 14.5h5" />
    </>
  ),
  'pill-check': (
    <>
      <path d="M9.2 19.2a4.4 4.4 0 0 1-6.2-6.2l6.2-6.2a4.4 4.4 0 0 1 6.2 6.2l-1.4 1.4" />
      <path d="M7.8 7.8l5.9 5.9" />
      <path d="m14.5 18.5 2 2 4-4" />
    </>
  ),
  'heart-pulse': (
    <>
      <path d="M12 20s-7.5-4.6-7.5-10A4.3 4.3 0 0 1 12 7.2 4.3 4.3 0 0 1 19.5 10c0 5.4-7.5 10-7.5 10z" />
      <path d="M7 12h2.4l1.2-2 2 4 1.2-2H17" />
    </>
  ),
  'refill-droplet': (
    <>
      <path d="M12 3.5s6 6.4 6 10.7a6 6 0 0 1-12 0c0-4.3 6-10.7 6-10.7z" />
      <path d="M12 11.5v5M9.5 14h5" />
    </>
  ),
  // -- Verzorging -----------------------------------------------------------
  shower: (
    <>
      <path d="M12 3v2" />
      <path d="M6 10a6 6 0 0 1 12 0z" />
      <path d="M7.5 14v1.5M12 14v2.5M16.5 14v1.5M9.8 18.5V20M14.2 18.5V20" />
    </>
  ),
  bath: (
    <>
      <path d="M4 12.5h16v2a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5z" />
      <path d="M6 12.5V5.5a2 2 0 0 1 4 0" />
      <path d="M7 19.5 6 21M17 19.5l1 1.5" />
    </>
  ),
  toothbrush: (
    <>
      <path d="M3.5 20.5 14 10" />
      <path d="M14 10l1.2-1.2.01-.01" />
      <path d="M15.2 5.6v2M17.4 4.8v2M19.6 4v2" />
      <path d="m14.6 9.4 1.8-1.8c1.5-1.5 3.4-2 3.4-2" />
    </>
  ),
  cream: (
    <>
      <path d="M8 8.5h8V19a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2z" />
      <path d="M9.5 5h5v3.5h-5z" />
      <path d="M11 8.5V10M13 8.5V10" />
    </>
  ),
  comb: (
    <>
      <path d="M4.5 19.5 15 9a3 3 0 0 1 4.5.5c.8 1 .5 2.4-.5 3.5L8.5 21" />
      <path d="m12.5 11.5 1.7 1.7M10.8 13.2l1.7 1.7M9.1 14.9l1.7 1.7M7.4 16.6l1.7 1.7" />
    </>
  ),
  razor: (
    <>
      <path d="M4 8.5h16a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-2a1 1 0 0 1 1-1z" />
      <path d="M12 12.5V21" />
      <path d="M6.5 10.5h11" />
    </>
  ),
  shirt: (
    <>
      <path d="M9 4 4.5 7l2 3.5L9 9v11h6V9l2.5 1.5 2-3.5L15 4a3 3 0 0 1-6 0z" />
    </>
  ),
  sunrise: (
    <>
      <path d="M4 18h16" />
      <path d="M7 18a5 5 0 0 1 10 0" />
      <path d="M12 6.5v2M5.6 9.6l1.4 1.4M18.4 9.6 17 11M3 14h2M19 14h2" />
      <path d="M9 21h6" />
    </>
  ),
  // -- Rust -----------------------------------------------------------------
  cushion: (
    <>
      <path d="M6 7h12a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z" />
      <path d="M6 7c-1-1-2 0-2 1M18 7c1-1 2 0 2 1M6 17c-1 1-2 0-2-1M18 17c1 1 2 0 2-1" />
    </>
  ),
  book: (
    <>
      <path d="M12 6.5C10.5 5 8.5 4.5 5.5 4.5c-.8 0-1.5.1-2 .3v14c.5-.2 1.2-.3 2-.3 3 0 5 .5 6.5 2 1.5-1.5 3.5-2 6.5-2 .8 0 1.5.1 2 .3v-14c-.5-.2-1.2-.3-2-.3-3 0-5 .5-6.5 2z" />
      <path d="M12 6.5v14" />
    </>
  ),
  pause: (
    <>
      <path d="M9 5v14M15 5v14" />
    </>
  ),
  armchair: (
    <>
      <path d="M7 11V7a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3v4" />
      <path d="M5 11a2 2 0 0 1 2 2v2h10v-2a2 2 0 1 1 4 0v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2z" />
      <path d="M7 19v2M17 19v2" />
    </>
  ),
  leaf: (
    <>
      <path d="M5 19C5 9 11 4 20 4c0 10-5 15-14 15" />
      <path d="M5 19c3-5 7-8.5 11-10.5" />
    </>
  ),
  mic: (
    <>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5.5 11a6.5 6.5 0 0 0 13 0" />
      <path d="M12 17.5V21M9 21h6" />
    </>
  ),
  'music-note': (
    <>
      <path d="M9 18.5V6l10-2.5V16" />
      <circle cx="6.5" cy="18.5" r="2.5" />
      <circle cx="16.5" cy="16" r="2.5" />
    </>
  ),
  'screen-off': (
    <>
      <rect x="7" y="4" width="10" height="16" rx="2" />
      <path d="M11 17.5h2" />
      <path d="m9 9 6 6M15 9l-6 6" />
    </>
  ),
  // -- Beweging -------------------------------------------------------------
  walk: (
    <>
      <circle cx="13.5" cy="4.5" r="1.8" />
      <path d="M13.5 8 11 10.5l2 3.5.5 6" />
      <path d="m11 10.5-2.5 1L7 14" />
      <path d="m13 14-3 2.5L8.5 21" />
      <path d="M13.5 8l2 2 2.5.5" />
    </>
  ),
  dumbbell: (
    <>
      <path d="M7 8v8M17 8v8M4 10v4M20 10v4M7 12h10" />
    </>
  ),
  lotus: (
    <>
      <path d="M12 17c-2 0-3.5-1.5-3.5-3.5C8.5 10 10 8 12 5.5 14 8 15.5 10 15.5 13.5c0 2-1.5 3.5-3.5 3.5z" />
      <path d="M4 18.5c2.7 1.5 5.3 2 8 2s5.3-.5 8-2" />
    </>
  ),
  stretch: (
    <>
      <circle cx="12" cy="4.6" r="1.7" />
      <path d="M12 8v6" />
      <path d="m12 9-4.5-2.5M12 9l4.5-2.5" />
      <path d="m12 14-2.5 6.5M12 14l2.5 6.5" />
    </>
  ),
  bike: (
    <>
      <circle cx="6" cy="16.5" r="3.5" />
      <circle cx="18" cy="16.5" r="3.5" />
      <path d="M6 16.5 9.5 9H15" />
      <path d="M9.5 9 12 16.5h6L14 9" />
      <path d="M14 6.5h2.5L15 9" />
    </>
  ),
  waves: (
    <>
      <path d="M3 10c2 0 2.5 1.5 4.5 1.5S10 10 12 10s2.5 1.5 4.5 1.5S19 10 21 10" />
      <path d="M3 15.5c2 0 2.5 1.5 4.5 1.5s2.5-1.5 4.5-1.5 2.5 1.5 4.5 1.5 2.5-1.5 4.5-1.5" />
      <circle cx="15" cy="6" r="1.8" />
      <path d="M8 6.5 12 8l3-1" />
    </>
  ),
  'tree-sun': (
    <>
      <circle cx="17.5" cy="6" r="2.2" />
      <path d="M8 21v-5" />
      <path d="M8 16a4 4 0 0 1-4-4c0-2.5 2-4 4-4 .3-2 2-3.5 4-3.5S15.7 6 16 8c1.8.3 3 1.7 3 3.5" />
    </>
  ),
  // -- Werk en studie -------------------------------------------------------
  briefcase: (
    <>
      <rect x="3.5" y="8" width="17" height="11.5" rx="2" />
      <path d="M9 8V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
      <path d="M3.5 13h17" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none" />
    </>
  ),
  monitor: (
    <>
      <rect x="3" y="4.5" width="18" height="12" rx="2" />
      <path d="M12 16.5V20M8.5 20h7" />
    </>
  ),
  mail: (
    <>
      <rect x="3" y="5.5" width="18" height="13" rx="2" />
      <path d="m3.5 7 8.5 6 8.5-6" />
    </>
  ),
  'mail-send': (
    <>
      <path d="M21 3.5 10.5 14" />
      <path d="M21 3.5 14.5 21l-4-7-7-4z" />
    </>
  ),
  phone: (
    <>
      <path d="M7.5 3.5h3l1.5 4.5-2 1.5a12 12 0 0 0 4.5 4.5l1.5-2 4.5 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 5.5 5.7a2 2 0 0 1 2-2.2z" />
    </>
  ),
  people: (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 19.5a5.5 5.5 0 0 1 11 0" />
      <circle cx="17" cy="9.5" r="2.2" />
      <path d="M16 14.6a4.4 4.4 0 0 1 4.5 4.4" />
    </>
  ),
  'people-chat': (
    <>
      <circle cx="8" cy="8.5" r="2.6" />
      <path d="M3.5 18a4.5 4.5 0 0 1 9 0" />
      <path d="M14.5 5.5h5a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5H18l-2.2 2v-2h-1.3a1.5 1.5 0 0 1-1.5-1.5V7a1.5 1.5 0 0 1 1.5-1.5z" />
    </>
  ),
  note: (
    <>
      <path d="M6 3.5h8.5L19 8v12.5H6z" />
      <path d="M14 3.5V8h5" />
      <path d="M9 12h6M9 15.5h6" />
    </>
  ),
  laptop: (
    <>
      <rect x="5" y="5" width="14" height="10" rx="1.5" />
      <path d="M2.5 18.5h19" />
    </>
  ),
  'note-pencil': (
    <>
      <path d="M6 4h7v4h4v12H6z" />
      <path d="M13 4l4 4" />
      <path d="m9.5 16.5 4.8-4.8 1.7 1.7-4.8 4.8H9.5z" />
    </>
  ),
  desk: (
    <>
      <path d="M3.5 9.5h17" />
      <path d="M5 9.5V20M19 9.5V20" />
      <path d="M13 13h4.5v7H13z" />
      <path d="M14.8 16.2h1" />
    </>
  ),
  cap: (
    <>
      <path d="m12 4 9.5 4L12 12 2.5 8z" />
      <path d="M6.5 10.5V15c0 1.7 2.5 3 5.5 3s5.5-1.3 5.5-3v-4.5" />
      <path d="M21.5 8v5" />
    </>
  ),
  // -- Huishouden -----------------------------------------------------------
  'box-tidy': (
    <>
      <path d="M4 8.5h16V19a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 19z" />
      <path d="M3 5.5h18v3H3z" />
      <path d="M10 12.5h4" />
    </>
  ),
  dishes: (
    <>
      <circle cx="9.5" cy="10.5" r="5.5" />
      <circle cx="9.5" cy="10.5" r="2" />
      <path d="M16.5 15.5c1.5 1 3.5.5 4-1" />
      <path d="M15 19c1.8 1.3 4.3.8 5.2-1" />
    </>
  ),
  washer: (
    <>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <circle cx="12" cy="13" r="4.5" />
      <path d="M12 10.5c1.2 0 2.2.6 2.8 1.5" />
      <path d="M6.5 6h2M16.5 6h1" />
    </>
  ),
  vacuum: (
    <>
      <path d="M6 20v-6a4 4 0 0 1 8 0v6" />
      <path d="M10 10V5.5A1.5 1.5 0 0 1 11.5 4h1A1.5 1.5 0 0 1 14 5.5V8" />
      <path d="M14 8c3 0 4.5 2 4.5 4.5V20" />
      <path d="M4 20h16" />
    </>
  ),
  bag: (
    <>
      <path d="M5.5 8h13l-1 11.5a2 2 0 0 1-2 1.8h-7a2 2 0 0 1-2-1.8z" />
      <path d="M9 10.5V6.8a3 3 0 0 1 6 0v3.7" />
    </>
  ),
  sparkle: (
    <>
      <path d="M12 3.5c.7 4.3 2.2 5.8 6.5 6.5-4.3.7-5.8 2.2-6.5 6.5-.7-4.3-2.2-5.8-6.5-6.5 4.3-.7 5.8-2.2 6.5-6.5z" />
      <path d="M18.5 15.5c.3 1.9 1 2.6 2.9 2.9-1.9.3-2.6 1-2.9 2.9-.3-1.9-1-2.6-2.9-2.9 1.9-.3 2.6-1 2.9-2.9z" />
    </>
  ),
  trash: (
    <>
      <path d="M4.5 6.5h15" />
      <path d="M9 6V4.5A1.5 1.5 0 0 1 10.5 3h3A1.5 1.5 0 0 1 15 4.5V6" />
      <path d="M6.5 6.5 7.4 19a2 2 0 0 0 2 1.8h5.2a2 2 0 0 0 2-1.8l.9-12.5" />
      <path d="M10.2 10.5v6M13.8 10.5v6" />
    </>
  ),
  plant: (
    <>
      <path d="M7 12.5h10l-1 7a2 2 0 0 1-2 1.7h-4a2 2 0 0 1-2-1.7z" />
      <path d="M12 12.5V9" />
      <path d="M12 9c0-2.5-2-4-4.5-4 0 2.5 2 4 4.5 4zM12 7.5c0-2 1.6-3.2 3.6-3.2 0 2-1.6 3.2-3.6 3.2z" />
    </>
  ),
  checklist: (
    <>
      <rect x="5" y="3.5" width="14" height="17" rx="2" />
      <path d="m8 9 1.2 1.2L11.5 8" />
      <path d="M13.5 9.2H16" />
      <path d="m8 14.5 1.2 1.2 2.3-2.2" />
      <path d="M13.5 14.7H16" />
    </>
  ),
  trowel: (
    <>
      <path d="M3.5 20.5 10 14" />
      <path d="M11.5 12.5c1.5-4 4.5-7 9-9-1 5-3.5 8-7.5 9.5z" />
      <path d="M13.5 10.5c1.2-2.3 2.8-4 5-5.5" />
    </>
  ),
  mop: (
    <>
      <path d="M12 3v10" />
      <path d="M12 13c-3 0-5 2-5 5v2.5h10V18c0-3-2-5-5-5z" />
      <path d="M9.5 16.5v4M12 16v4.5M14.5 16.5v4" />
    </>
  ),
  dryer: (
    <>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <circle cx="12" cy="13" r="4.5" />
      <path d="M10 13c.8-1 3.2-1 4 0" />
      <path d="M6.5 6h1.5M16 6h1.5" />
    </>
  ),
  'bed-refresh': (
    <>
      <path d="M3 18v-8M3 14h18v4" />
      <path d="M3 16.5h18" />
      <path d="M6 14v-1.5A1.5 1.5 0 0 1 7.5 11h2A1.5 1.5 0 0 1 11 12.5V14" />
      <path d="m15.5 6 1.8-1.8L19 6" />
      <path d="M17.3 4.2V9" />
    </>
  ),
  // -- Sociaal --------------------------------------------------------------
  chats: (
    <>
      <path d="M8.5 4h8A2.5 2.5 0 0 1 19 6.5v4a2.5 2.5 0 0 1-2.5 2.5H14l-3 3v-3h-.5" />
      <path d="M11 15.5V17a2.5 2.5 0 0 1-2.5 2.5H7l-2 2v-2.5A2.5 2.5 0 0 1 2.5 17v-4A2.5 2.5 0 0 1 5 10.5h3" />
    </>
  ),
  'phone-waves': (
    <>
      <path d="M7.5 3.5h3l1.5 4.5-2 1.5a12 12 0 0 0 4.5 4.5l1.5-2 4.5 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 5.5 5.7a2 2 0 0 1 2-2.2z" />
      <path d="M15 3.8a5 5 0 0 1 4.2 4.2M15 7a2 2 0 0 1 1.7 1.7" />
    </>
  ),
  chat: (
    <>
      <path d="M12 4c5 0 9 3.1 9 7s-4 7-9 7c-1 0-2-.1-2.9-.4L5 19.5l1-3.2C4.3 15 3 13.2 3 11c0-3.9 4-7 9-7z" />
    </>
  ),
  balloon: (
    <>
      <path d="M12 3.5c3.3 0 5.5 2.4 5.5 5.4 0 3.6-3.2 6.6-5.5 6.6s-5.5-3-5.5-6.6c0-3 2.2-5.4 5.5-5.4z" />
      <path d="M12 15.5V18c0 1.5-1 1.5-1 3" />
    </>
  ),
  store: (
    <>
      <path d="M4.5 9.5 6 4.5h12l1.5 5" />
      <path d="M4.5 9.5a2.4 2.4 0 0 0 4.8 0 2.4 2.4 0 0 0 4.9 0 2.4 2.4 0 0 0 4.8 0" />
      <path d="M6 12v8h12v-8" />
      <path d="M10 20v-5h4v5" />
    </>
  ),
  message: (
    <>
      <path d="M12 3.5c5 0 9 3.1 9 7s-4 7-9 7c-1 0-2-.1-2.9-.4L5 18.9l1-3.1C4.3 14.5 3 12.7 3 10.5c0-3.9 4-7 9-7z" />
      <path d="M8.5 10.5h.01M12 10.5h.01M15.5 10.5h.01" />
    </>
  ),
  // -- Therapie en zelfzorg -------------------------------------------------
  'chat-heart': (
    <>
      <path d="M12 4c5 0 9 3.1 9 7s-4 7-9 7c-1 0-2-.1-2.9-.4L5 19.5l1-3.2C4.3 15 3 13.2 3 11c0-3.9 4-7 9-7z" />
      <path d="M12 13.8s-2.8-1.7-2.8-3.4a1.6 1.6 0 0 1 2.8-1 1.6 1.6 0 0 1 2.8 1c0 1.7-2.8 3.4-2.8 3.4z" />
    </>
  ),
  'person-lotus': (
    <>
      <circle cx="12" cy="5" r="1.8" />
      <path d="M12 8v5" />
      <path d="m12 9.5-4 2M12 9.5l4 2" />
      <path d="M7 17c1.5-1 3-1.5 5-1.5s3.5.5 5 1.5c-1.5 1.5-3.2 2.2-5 2.2s-3.5-.7-5-2.2z" />
    </>
  ),
  wind: (
    <>
      <path d="M3 8h9a2.5 2.5 0 1 0-2.4-3.2" />
      <path d="M3 12.5h14a2.8 2.8 0 1 1-2.6 3.7" />
      <path d="M3 17h6" />
    </>
  ),
  'heart-hand': (
    <>
      <path d="M12 20.5s-7.5-4.6-7.5-10A4.3 4.3 0 0 1 12 7.2 4.3 4.3 0 0 1 19.5 10c0 5.4-7.5 10-7.5 10z" />
      <path d="M9 12.5c1 1.4 2 2.2 3 2.2s2-.8 3-2.2" />
    </>
  ),
  journal: (
    <>
      <path d="M6 3.5h12A1.5 1.5 0 0 1 19.5 5v14a1.5 1.5 0 0 1-1.5 1.5H6z" />
      <path d="M6 3.5A1.5 1.5 0 0 0 4.5 5v14A1.5 1.5 0 0 0 6 20.5" />
      <path d="M9.5 8h6M9.5 11.5h6M9.5 15h3.5" />
    </>
  ),
  'heart-sparkle': (
    <>
      <path d="M11 19.5S4.5 15.6 4.5 10.6A3.8 3.8 0 0 1 11 8a3.8 3.8 0 0 1 6.5 2.6c0 5-6.5 8.9-6.5 8.9z" />
      <path d="M18.5 3c.3 1.6.9 2.2 2.5 2.5-1.6.3-2.2.9-2.5 2.5-.3-1.6-.9-2.2-2.5-2.5 1.6-.3 2.2-.9 2.5-2.5z" />
    </>
  ),
  // -- Buiten en dier -------------------------------------------------------
  paw: (
    <>
      <ellipse cx="12" cy="15.5" rx="3.4" ry="2.8" />
      <circle cx="6.8" cy="10.5" r="1.6" />
      <circle cx="10.2" cy="7.8" r="1.6" />
      <circle cx="13.8" cy="7.8" r="1.6" />
      <circle cx="17.2" cy="10.5" r="1.6" />
    </>
  ),
  tree: (
    <>
      <path d="M12 21v-7" />
      <path d="M12 3.5 7 10h3l-4 5.5h12L14 10h3z" />
    </>
  ),
  flower: (
    <>
      <circle cx="12" cy="8.5" r="2" />
      <circle cx="8.8" cy="6.3" r="1.7" />
      <circle cx="15.2" cy="6.3" r="1.7" />
      <circle cx="8.8" cy="10.7" r="1.7" />
      <circle cx="15.2" cy="10.7" r="1.7" />
      <path d="M12 13.5V21" />
      <path d="M12 17c-1.8 0-3-.8-3.5-2" />
    </>
  ),
  'plant-pot': (
    <>
      <path d="M7.5 13h9l-.9 6.5a2 2 0 0 1-2 1.7h-3.2a2 2 0 0 1-2-1.7z" />
      <path d="M12 13V9.5" />
      <path d="M12 9.5c-2.5 0-4-1.5-4-4 2.5 0 4 1.5 4 4zM12 9.5c2.5 0 4-1.5 4-4-2.5 0-4 1.5-4 4z" />
    </>
  ),
  // -- Slapen ---------------------------------------------------------------
  moon: (
    <>
      <path d="M20 13.5A8 8 0 1 1 10.5 4a6.5 6.5 0 0 0 9.5 9.5z" />
    </>
  ),
  zzz: (
    <>
      <path d="M4 6h5l-5 5.5h5" />
      <path d="M13 10h4l-4 4.5h4" />
      <path d="M18 15h3l-3 3.5h3" />
    </>
  ),
  bed: (
    <>
      <path d="M3 18V6.5" />
      <path d="M3 13.5h18V18" />
      <path d="M3 16h18" />
      <path d="M6 13.5v-1.8A1.7 1.7 0 0 1 7.7 10h8.6a4.7 4.7 0 0 1 4.7 3.5" />
    </>
  ),
  'moon-stars': (
    <>
      <path d="M18.5 14.5A7 7 0 1 1 9.5 5.5a5.6 5.6 0 0 0 9 9z" />
      <path d="M17.5 3c.2 1.1.6 1.5 1.7 1.7-1.1.2-1.5.6-1.7 1.7-.2-1.1-.6-1.5-1.7-1.7 1.1-.2 1.5-.6 1.7-1.7z" />
    </>
  ),
  // -- Reizen en verplaatsing ----------------------------------------------
  'door-out': (
    <>
      <path d="M14 3.5H6A1.5 1.5 0 0 0 4.5 5v14A1.5 1.5 0 0 0 6 20.5h8" />
      <path d="M10.5 12h10" />
      <path d="m17.5 8.5 3 3.5-3 3.5" />
    </>
  ),
  home: (
    <>
      <path d="m4 11 8-7 8 7" />
      <path d="M6 9.5V20h12V9.5" />
      <path d="M10 20v-6h4v6" />
    </>
  ),
  suitcase: (
    <>
      <rect x="4.5" y="8" width="15" height="12" rx="2" />
      <path d="M9.5 8V6a2 2 0 0 1 2-2h1a2 2 0 0 1 2 2v2" />
      <path d="M9.5 12v4M14.5 12v4" />
    </>
  ),
  scooter: (
    <>
      <circle cx="6" cy="17.5" r="2.5" />
      <circle cx="18" cy="17.5" r="2.5" />
      <path d="M8.5 17.5h7" />
      <path d="M18 17.5 15.5 6H13" />
      <path d="M6 17.5 9 10h3.5" />
    </>
  ),
  'house-arrow': (
    <>
      <path d="m3.5 10.5 7-6 7 6" />
      <path d="M5.5 9v9.5h4.5" />
      <path d="M13 18.5h8M18 15.5l3 3-3 3" />
    </>
  ),
  // -- Administratie --------------------------------------------------------
  folder: (
    <>
      <path d="M3.5 6.5A1.5 1.5 0 0 1 5 5h4l2 2.5h8A1.5 1.5 0 0 1 20.5 9v9A1.5 1.5 0 0 1 19 19.5H5A1.5 1.5 0 0 1 3.5 18z" />
    </>
  ),
  package: (
    <>
      <path d="m12 3 8 4.2v9.6L12 21l-8-4.2V7.2z" />
      <path d="m4.3 7.2 7.7 4 7.7-4" />
      <path d="M12 11.2V21" />
    </>
  ),
  plug: (
    <>
      <path d="M9 3.5V8M15 3.5V8" />
      <path d="M6.5 8h11v3a5.5 5.5 0 0 1-11 0z" />
      <path d="M12 16.5V21" />
    </>
  ),
  backpack: (
    <>
      <path d="M7 8a5 5 0 0 1 10 0v11a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2z" />
      <path d="M9.5 8V6a2.5 2.5 0 0 1 5 0v2" />
      <path d="M7 13.5h10" />
      <path d="M10 17h4" />
    </>
  ),
  documents: (
    <>
      <path d="M8 6.5h9.5L21 10v10H8z" />
      <path d="M17 6.5V10h4" />
      <path d="M6 3.5h9.5L18 5.5" />
      <path d="M6 3.5A1.5 1.5 0 0 0 4.5 5v12H6" />
      <path d="M11 13.5h6M11 16.5h4" />
    </>
  ),
  // -- Creatief -------------------------------------------------------------
  palette: (
    <>
      <path d="M12 3.5a8.5 8.5 0 1 0 0 17c1.4 0 2-.8 2-1.8 0-1.4-1.3-1.7-1.3-2.9 0-1 .8-1.8 2.3-1.8h1.5a3.5 3.5 0 0 0 3.5-3.5c0-4.1-4-7-8-7z" />
      <circle cx="7.5" cy="10.5" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="10.5" cy="7.5" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="14.5" cy="8" r="1.1" fill="currentColor" stroke="none" />
    </>
  ),
  pencil: (
    <>
      <path d="m5 19 .8-3.6L15.6 5.6a1.8 1.8 0 0 1 2.5 0l.3.3a1.8 1.8 0 0 1 0 2.5L8.6 18.2z" />
      <path d="m14.5 6.7 2.8 2.8" />
    </>
  ),
  brush: (
    <>
      <path d="M20.5 3.5 11 13" />
      <path d="M8.5 12.5a3.5 3.5 0 0 1 3.5 3.5c0 2.5-2 4.5-5 4.5-1.5 0-2.7-.5-3.5-1 1-.8 1.5-1.8 1.5-3a3.5 3.5 0 0 1 3.5-4z" />
    </>
  ),
  camera: (
    <>
      <path d="M4 8.5A1.5 1.5 0 0 1 5.5 7h2L9 5h6l1.5 2h2A1.5 1.5 0 0 1 20 8.5V18a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18z" />
      <circle cx="12" cy="13" r="3.5" />
    </>
  ),
  // -- Afspraken ------------------------------------------------------------
  calendar: (
    <>
      <rect x="3.5" y="5.5" width="17" height="15" rx="2" />
      <path d="M3.5 10h17" />
      <path d="M8 3.5v4M16 3.5v4" />
      <path d="M8 14h2.5M13.5 14H16M8 17h2.5" />
    </>
  ),
  'medical-cross': (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 8v8M8 12h8" />
    </>
  ),
  tooth: (
    <>
      <path d="M8 4C5.5 4 4 6 4 8.5c0 4 2 9.5 3.5 9.5 1.2 0 1-3.5 2-5 .6-.9 1.4-1.3 2.5-1.3s1.9.4 2.5 1.3c1 1.5.8 5 2 5 1.5 0 3.5-5.5 3.5-9.5C20 6 18.5 4 16 4c-1.7 0-2.4 1-4 1S9.7 4 8 4z" />
    </>
  ),
  'hand-heart': (
    <>
      <path d="M12 21s-6.5-4-6.5-8.7A3.7 3.7 0 0 1 12 9.6a3.7 3.7 0 0 1 6.5 2.7C18.5 17 12 21 12 21z" />
      <path d="M3 8.5c2-1.2 3.5-1.2 5-.4M21 8.5c-2-1.2-3.5-1.2-5-.4" />
      <path d="M3 8.5V5.8M21 8.5V5.8" />
    </>
  ),
  'calendar-chat': (
    <>
      <rect x="3.5" y="5.5" width="17" height="15" rx="2" />
      <path d="M3.5 10h17" />
      <path d="M8 3.5v4M16 3.5v4" />
      <path d="M9 13h6a1.5 1.5 0 0 1 1.5 1.5v1.5A1.5 1.5 0 0 1 15 17.5h-3l-2 1.8v-1.8H9A1.5 1.5 0 0 1 7.5 16v-1.5A1.5 1.5 0 0 1 9 13z" />
    </>
  ),
  // -- Extra ----------------------------------------------------------------
  gamepad: (
    <>
      <path d="M7 8h10a5 5 0 0 1 4.9 5.9l-.5 2.6a2.8 2.8 0 0 1-4.8 1.3L15 16H9l-1.6 1.8a2.8 2.8 0 0 1-4.8-1.3l-.5-2.6A5 5 0 0 1 7 8z" />
      <path d="M8.5 10.8v3M7 12.3h3" />
      <circle cx="15.8" cy="11.3" r="0.7" fill="currentColor" stroke="none" />
      <circle cx="17.8" cy="13.3" r="0.7" fill="currentColor" stroke="none" />
    </>
  ),
  // -- Fallback -------------------------------------------------------------
  dot: (
    <circle cx="12" cy="12" r="3.5" />
  ),
}

/** Fallback glyph id for unknown iconIds. */
export const FALLBACK_GLYPH_ID = 'dot'
